// Caisse du régisseur (H4-07, D-014) : reçus provisoires du jour sur une créance ouverte (régisseur
// de la caisse seulement), annulation motivée avant clôture, clôture journalière (montant constaté,
// écart, bordereau PDF par la tâche de fond), clôtures passées ; vérification par le financier,
// distinct du régisseur, qui crée un paiement confirmé et une quittance par reçu.
import { Bandeau, Bouton, Filtres, Lien, LienBouton, Panneau, PuceEtat, Tableau, useFiltresPersistes, type ColonneTableau } from '@cc/ui';
import { ArrowLeft, FileDown, Plus } from 'lucide-react';
import { useState } from 'react';
import { useParams } from 'react-router';
import { messageErreur } from '../../api/erreurs';
import { EnTetePage } from '../../commun/EnTetePage';
import { EtatRequete } from '../../commun/EtatRequete';
import { date, dateHeure, isoJour } from '../../commun/format';
import { LIBELLES_ETAT_RECU, francs, libelle, tonRecu, type ClotureCaisse, type RecuCaisse } from './donnees';
import { FormulaireAction } from './FormulaireAction';
import { NavigationFinances, useDroitsRecettes } from './NavigationFinances';
import { telechargerFichier, useCaisses, useClotures, useCreances, useRecus } from './requetes';
import commun from '../../commun/commun.module.css';
import styles from './recettes.module.css';

function BoutonBordereau({ cl }: { cl: ClotureCaisse }) {
  const [erreur, setErreur] = useState<string | null>(null);
  if (!cl.bordereau_document_id) return <span className={styles.sous}>bordereau en cours de production</span>;
  return (
    <>
      <Bouton petit variante="secondaire" icone={FileDown} onClick={() => telechargerFichier(`/recettes/caisses/clotures/${cl.id}/bordereau`, `bordereau-${cl.date}.pdf`).catch((e: unknown) => setErreur(messageErreur(e)))}>
        Bordereau
      </Bouton>
      {erreur ? <span className={styles.negatif}> {erreur}</span> : null}
    </>
  );
}

export function PageCaisse() {
  const { id = '' } = useParams();
  const droits = useDroitsRecettes();
  const caisses = useCaisses();
  const caisse = (caisses.data ?? []).find((k) => k.id === id);
  const [filtres, setFiltres] = useFiltresPersistes('recettes-caisse-recus', { jour: 'oui' });
  const aujourdhui = isoJour(new Date());
  const recus = useRecus(id, filtres.jour === 'oui' ? { date: aujourdhui } : {});
  const clotures = useClotures(id);
  const regisseur = Boolean(caisse && caisse.regisseur_appartenance_id === droits.moi);
  const creances = useCreances({ etat: '', echue: '', rubrique: '', nature: '' }, regisseur && droits.caisseCreer);
  const ouvertes = (creances.data ?? []).filter((c) => c.etat === 'emise' || c.etat === 'partiellement_payee');
  const duJour = (recus.data ?? []).filter((r) => r.etat === 'emis');
  const totalDuJour = duJour.reduce((s, r) => s + r.montant, 0);
  const dejaCloturee = (clotures.data ?? []).some((cl) => cl.date === aujourdhui);

  const colonnesRecus: ColonneTableau<RecuCaisse>[] = [
    { id: 'numero', entete: 'Reçu', enTeteLigne: true, masquable: false, valeurTri: (r) => r.numero, cellule: (r) => <span className={styles.mono}>{r.numero}</span> },
    { id: 'creance', entete: 'Créance', cellule: (r) => <Lien href={`/recettes/creances/${r.creance_id}`} className={styles.mono}>{r.creance_numero ?? r.creance_id.slice(0, 8)}</Lien> },
    { id: 'montant', entete: 'Montant', numerique: true, valeurTri: (r) => r.montant, cellule: (r) => <span className={styles.montant}>{francs(r.montant)}</span> },
    { id: 'emis', entete: 'Émis le', valeurTri: (r) => r.emis_le, cellule: (r) => dateHeure(r.emis_le) },
    { id: 'etat', entete: 'État', valeurTri: (r) => r.etat, cellule: (r) => (
      <>
        <PuceEtat ton={tonRecu(r.etat)} libelle={libelle(LIBELLES_ETAT_RECU, r.etat)} />
        {r.motif_annulation ? <div className={styles.sous}>{r.motif_annulation}</div> : null}
        {r.paiement_id ? <div className={styles.sous}><Lien href={`/recettes/paiements/${r.paiement_id}`}>Paiement de caisse</Lien></div> : null}
      </>
    ) },
    { id: 'actions', entete: 'Actions', masquable: false, cellule: (r) => (r.etat === 'emis' && droits.caisseModifier && (regisseur || droits.modifier) ? (
      <FormulaireAction libelle="Annuler" titre={`Annuler le reçu ${r.numero}`} explication="Possible avant la clôture seulement ; après, l'annulation passe par un remboursement." chemin={`/recettes/caisses/recus/${r.id}/annuler`} champs={[{ nom: 'motif', libelle: 'Motif', type: 'multiligne', obligatoire: true }]} succes="Reçu annulé." variante="discret" petit />
    ) : null) },
  ];
  const colonnesClotures: ColonneTableau<ClotureCaisse>[] = [
    { id: 'date', entete: 'Journée', enTeteLigne: true, masquable: false, valeurTri: (cl) => cl.date, cellule: (cl) => date(cl.date) },
    { id: 'recus', entete: 'Reçus', numerique: true, cellule: (cl) => String(cl.nombre_recus) },
    { id: 'total', entete: 'Total des reçus', numerique: true, cellule: (cl) => <span className={styles.montant}>{francs(cl.total_recus)}</span> },
    { id: 'constate', entete: 'Constaté', numerique: true, cellule: (cl) => <span className={styles.montant}>{francs(cl.montant_constate)}</span> },
    { id: 'ecart', entete: 'Écart', numerique: true, valeurTri: (cl) => cl.ecart, cellule: (cl) => <span className={`${styles.montant} ${styles.ecart}`} data-nul={cl.ecart === 0 || undefined}>{cl.ecart === 0 ? 'aucun' : francs(cl.ecart)}</span> },
    { id: 'verification', entete: 'Vérification', cellule: (cl) => (cl.verifiee_le ? <PuceEtat ton="ok" libelle={`Vérifiée le ${date(cl.verifiee_le)}`} /> : <PuceEtat ton="signer" libelle="À vérifier par le financier" />) },
    { id: 'actions', entete: 'Actions', masquable: false, cellule: (cl) => (
      <span className={styles.actions}>
        <BoutonBordereau cl={cl} />
        {!cl.verifiee_le && droits.modifier && !regisseur ? (
          <FormulaireAction<ClotureCaisse> libelle="Vérifier" titre={`Vérifier la clôture du ${date(cl.date)}`} explication={`Chaque reçu clôturé (${cl.nombre_recus}, ${francs(cl.total_recus)}) devient un paiement confirmé de canal caisse, imputé sur sa créance, avec une quittance. Règle des quatre yeux : le régisseur ne vérifie pas sa propre caisse.`} chemin={`/recettes/caisses/clotures/${cl.id}/verifier`} succes={(r) => `Clôture vérifiée : ${r.quittances?.length ?? 0} quittance(s) émise(s).`} petit />
        ) : null}
      </span>
    ) },
  ];

  return (
    <>
      <EnTetePage
        etiquette="Finances, caisse"
        titre={caisse ? caisse.libelle : 'Caisse'}
        description={caisse ? `${caisse.lieu} ; régisseur : ${caisse.regisseur}${regisseur ? ' (vous)' : ''} ; ${francs(caisse.a_verifier)} à vérifier.` : null}
        actions={
          <>
            <LienBouton href="/recettes/caisses" icone={ArrowLeft}>
              Caisses
            </LienBouton>
            {regisseur && droits.caisseCreer && caisse?.active ? (
              <FormulaireAction<RecuCaisse>
                libelle="Émettre un reçu"
                titre="Reçu provisoire"
                explication="Le reçu porte sur une créance ouverte, borné au reste à encaisser (reçus et paiements en attente déduits). Il devient un paiement confirmé à la vérification de la clôture."
                chemin={`/recettes/caisses/${id}/recus`}
                icone={Plus}
                champs={[
                  { nom: 'creance_id', libelle: 'Créance', type: 'selection', obligatoire: true, options: ouvertes.map((c) => ({ valeur: c.id, libelle: `${c.numero}, ${c.redevable.nom}, ${c.libelle}, reste ${francs(c.reste)}` })) },
                  { nom: 'montant', libelle: 'Montant encaissé (F)', type: 'nombre', obligatoire: true },
                ]}
                succes={(r) => `Reçu ${r.numero} émis pour ${francs(r.montant)}.`}
              />
            ) : null}
            {regisseur && droits.caisseModifier && caisse?.active && !dejaCloturee ? (
              <FormulaireAction<ClotureCaisse>
                libelle="Clôturer la journée"
                titre={`Clôturer la journée du ${date(aujourdhui)}`}
                explication={`${duJour.length} reçu(s) émis aujourd'hui pour ${francs(totalDuJour)}. Saisissez le montant constaté en caisse : l'écart est calculé et, s'il n'est pas nul, une anomalie est ouverte. Le bordereau PDF est produit par la tâche de fond.`}
                chemin={`/recettes/caisses/${id}/cloturer`}
                champs={[{ nom: 'montant_constate', libelle: 'Montant constaté (F)', type: 'nombre', obligatoire: true, defaut: String(totalDuJour) }]}
                succes={(cl) => (cl.ecart === 0 ? 'Journée clôturée sans écart.' : `Journée clôturée avec un écart de ${francs(cl.ecart)} ; une anomalie est ouverte.`)}
                variante="secondaire"
              />
            ) : null}
          </>
        }
      />
      <NavigationFinances />
      <EtatRequete chargement={caisses.isPending} erreur={caisses.error} onReessayer={() => void caisses.refetch()} lot="L2-A" fonction="La caisse">
        {!caisse ? <Bandeau type="erreur">Caisse introuvable ou non visible avec vos droits.</Bandeau> : null}
        {caisse && !regisseur ? <Bandeau type="info">Seul le régisseur de cette caisse émet des reçus et clôture la journée ; le financier vérifie les clôtures.</Bandeau> : null}
        {caisse ? (
          <div className={commun.pile}>
            <Panneau titre="Reçus provisoires" actions={<Filtres libelle="Période des reçus" groupes={[{ id: 'jour', libelle: 'Afficher', options: [{ valeur: 'oui', libelle: 'Aujourd\'hui' }, { valeur: '', libelle: 'Tous' }] }]} valeurs={filtres} onChanger={setFiltres} />}>
              <EtatRequete chargement={recus.isPending} erreur={recus.error} onReessayer={() => void recus.refetch()} fonction="Les reçus">
                {(recus.data ?? []).length === 0 ? <p className={commun.na}>Aucun reçu {filtres.jour === 'oui' ? 'aujourd\'hui' : ''}.</p> : <Tableau legende="Reçus provisoires" colonnes={colonnesRecus} lignes={recus.data ?? []} cleLigne={(r) => r.id} />}
              </EtatRequete>
              <p className={styles.sous}>
                Total des reçus émis du jour : <strong>{francs(totalDuJour)}</strong>{dejaCloturee ? ' ; journée déjà clôturée.' : ''}
              </p>
            </Panneau>
            <Panneau titre="Clôtures journalières">
              <EtatRequete chargement={clotures.isPending} erreur={clotures.error} onReessayer={() => void clotures.refetch()} fonction="Les clôtures">
                {(clotures.data ?? []).length === 0 ? <p className={commun.na}>Aucune clôture.</p> : <Tableau legende="Clôtures" colonnes={colonnesClotures} lignes={clotures.data ?? []} cleLigne={(cl) => cl.id} />}
              </EtatRequete>
            </Panneau>
          </div>
        ) : null}
      </EtatRequete>
    </>
  );
}
